<script setup lang="ts">
import { $isListNode, ListNode, type SerializedListNode } from "@lexical/list";
import { mergeRegister } from "@lexical/utils";
import type { PasteCommandType, SerializedLexicalNode } from "lexical";
import { $getNodeByKey, COMMAND_PRIORITY_LOW, PASTE_COMMAND } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { onMounted, onUnmounted } from "vue";

import { isSerializedTextNode } from "~/utils/common";

const editor = useLexicalComposer();

const handleListNodeMutations = (mutatedNodes: Map<string, string>) =>
  editor.update(() => {
    const mutatedLists = [...mutatedNodes.keys()].map((e) => $getNodeByKey(e)).filter($isListNode);
    mutatedLists.forEach((oldList) => {
      const listType = oldList.getListType();
      let curr = oldList;
      const prev = oldList.getPreviousSibling();
      if (prev && $isListNode(prev) && prev.getListType() === listType) {
        prev.append(...oldList.getChildren());
        oldList.remove();
        curr = prev;
      }
      const next = curr.getNextSibling();
      if (next && $isListNode(next) && next.getListType() === listType) {
        curr.append(...next.getChildren());
        next.remove();
      }
    });
  });

const hasNonEmptyText = (node: SerializedLexicalNode): boolean => {
  if (isSerializedTextNode(node)) {
    return node.text.trim().length > 0;
  }

  if ("children" in node && Array.isArray(node.children)) {
    return node.children.some((child) => hasNonEmptyText(child));
  }

  return false;
};

const isEmptyListItem = (node: SerializedLexicalNode) => node.type === "listitem" && !hasNonEmptyText(node);

const cleanChecklistNode = (node: SerializedLexicalNode): SerializedLexicalNode | SerializedListNode | null => {
  const isChecklistNode = node.type === "list" && "listType" in node && node.listType === "check";

  if (!isChecklistNode || !("children" in node) || !Array.isArray(node.children)) {
    return node;
  }

  const cleanedChildren = node.children
    .map((child) => cleanChecklistNode(child))
    .filter((child): child is SerializedLexicalNode => child !== null && !isEmptyListItem(child));

  if (cleanedChildren.length === 0) {
    return null;
  }

  return { ...node, children: cleanedChildren };
};

const cleanChecklistNodes = (nodes: SerializedLexicalNode[]) =>
  nodes.map((node) => cleanChecklistNode(node)).filter((node): node is SerializedLexicalNode => node !== null);

let unregisterListeners: () => void;

onMounted(() => {
  if (!editor.hasNodes([ListNode])) {
    return;
  }
  unregisterListeners = mergeRegister(
    editor.registerMutationListener(ListNode, handleListNodeMutations),
    editor.registerCommand<PasteCommandType>(
      PASTE_COMMAND,
      (event) => {
        if ((event as ClipboardEvent & { isModified?: boolean }).isModified) {
          return false;
        }

        const clipboardData = event instanceof ClipboardEvent ? event.clipboardData : null;
        const lexicalData = clipboardData?.getData("text/x-dart-editor");
        if (!lexicalData) {
          return false;
        }

        const data = JSON.parse(lexicalData);
        const cleaned = cleanChecklistNodes(data.nodes);
        if (JSON.stringify(cleaned) === JSON.stringify(data.nodes)) {
          return false;
        }

        event.preventDefault();
        const dataTransfer = new DataTransfer();
        dataTransfer.setData("text/x-dart-editor", JSON.stringify({ ...data, nodes: cleaned }));
        const modified: ClipboardEvent & { isModified?: boolean } = new ClipboardEvent("paste", {
          clipboardData: dataTransfer,
        });
        modified.isModified = true;
        editor.dispatchCommand(PASTE_COMMAND, modified);
        return true;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});
</script>

<template>
  <slot />
</template>
